import { useCallback, useState } from 'react';

import { ClientDirectorBookingModal } from '~/modules-rocco/booking';
import type { ProjectPageLoader } from '@modules/project/type';

type ClientDirectorCalendarSession =
    ProjectPageLoader['project']['ClientDirectorCalendarSession'];

/**
 * Shared "Book a Meeting" wiring for the project detail page. A project links a
 * Client Director meeting session via `ClientDirectorCalendarSession`; when that
 * session is active and one-on-one the CTA should open the real booking modal.
 *
 * Takes the linked session as an argument (rather than reading the loader) so
 * the desktop and mobile CTA call-sites — which already hold the project — can
 * share a single source of truth and stay renderable outside a data router.
 * Each renders its own native button wired to `openBookingModal` and drops
 * `bookingModal` into its tree.
 */
export const useProjectBookMeeting = (
    session: ClientDirectorCalendarSession,
) => {
    const canBookDirect = Boolean(
        session?.IsActive && session.MeetingType === 'one-on-one',
    );

    const [open, setOpen] = useState(false);
    const openBookingModal = useCallback(() => setOpen(true), []);

    const bookingModal =
        canBookDirect && session ? (
            <ClientDirectorBookingModal
                sessionId={session.ID}
                open={open}
                onOpenChange={setOpen}
            />
        ) : null;

    return { canBookDirect, openBookingModal, bookingModal };
};
